import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react'
import LayoutContainer from 'src/components/common/containers/LayoutContainer'
import SaveNotification from 'src/components/common/SaveNotification'
import { fetcher, getRequest, patchRequest, putRequest, staticOptions } from 'src/lib/core/api-client'
import { apiURI } from 'src/lib/utils/constants'
import { useLoader } from 'src/lib/contexts/LoaderContext'
import InputTextField, { Input } from '../common/form-elements/InputTextField'
import { useLocale } from 'src/lib/hooks/useLocal'
import { UserProfileState, UserProfileErrorState } from 'src/lib/types/settings'
import { defaultUserProfile, defaultUserProfileErrorState } from 'src/assets/SettingsConfig'
import { errorToast, successToast } from 'src/lib/utils/toasters'
import { useUser } from 'src/lib/contexts/UserContext'
import 'react-phone-input-2/lib/style.css'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import { roleValues } from 'src/lib/utils/user'
import PhoneNumberInputField from '../common/form-elements/PhoneNumberInputField'
import { validateEmail } from 'src/lib/utils/helpers'
import { Typography } from '@mui/material'
import { regexPatterns } from 'src/lib/utils/validations'
import { useTemporary } from 'src/lib/contexts/TemporaryContext'
import useConfirmOnRouteChanges from 'src/lib/utils/custom-hooks/useConfirmOnRouteChanges'
import { useOrganisationContext } from 'src/lib/contexts/OrganisationContext'
import { Info } from 'react-feather'

const MyProfile = () => {
  const dropdownStyle = {
    fontWeight: 400,
    fontSize: 14
  }
  const inputStyle = { style: { fontSize: '14px' } }
  const { t } = useLocale()
  const { user, setUserCtx } = useUser()
  const { setLoaderProperties } = useLoader()
  const { temporary, setTemporaryData } = useTemporary()

  const {
    getters: { organisationData }
  } = useOrganisationContext()
  const [userProfile, setUserProfile] = useState<UserProfileState>(defaultUserProfile)
  const [initialUserProfile, setInitialUserProfile] = useState<UserProfileState>(defaultUserProfile)
  const [userProfileErrors, setUserProfileErrors] = useState<UserProfileErrorState>(defaultUserProfileErrorState)

  const isUserConfigured = useMemo(() => {
    return Boolean(user?.firstName) && Boolean(user?.phoneNumber) && Boolean(user?.role)
  }, [user])

  useEffect(() => {
    if (!user) return
    setLoaderProperties({ isLoaderVisible: true, message: t('loading') })
    setUserProfile({
      ...userProfile,
      firstName: user?.firstName ?? defaultUserProfile?.firstName,
      lastName: user?.lastName ?? defaultUserProfile?.lastName,
      phoneNumber: user?.phoneNumber ? user?.phoneNumber?.substring(1) : defaultUserProfile?.phoneNumber,
      email: user?.email ?? defaultUserProfile?.email,
      role: user?.role ?? defaultUserProfile?.role
    })
    setInitialUserProfile({
      ...userProfile,
      firstName: (user as any)?.firstName ?? defaultUserProfile?.firstName,
      lastName: user?.lastName ?? defaultUserProfile?.lastName,
      phoneNumber: (user as any)?.phoneNumber?.substring(1) ?? defaultUserProfile?.phoneNumber,
      email: (user as any)?.email ?? defaultUserProfile?.email,
      role: (user as any)?.role ?? defaultUserProfile?.role
    })
    setLoaderProperties({ isLoaderVisible: false })
  }, [user])

  useEffect(() => {
    if (temporary) {
      setUserProfile({ ...temporary })
      setTemporaryData(undefined)
    }
  }, [temporary])

  useConfirmOnRouteChanges(
    JSON.stringify(initialUserProfile) !== JSON.stringify(userProfile),
    () => {
      return new Promise((resolve: any, reject) => {
        if (confirm('You have unsaved changes. Do you want to stay on the page?')) {
          setTemporaryData(userProfile)
          resolve(true)
        } else {
          reject(false)
        }
      })
    },
    userProfile
  )

  const isFromShopifyPublicApp = organisationData?.isFromShopifyPublicApp

  const isValid = () => {
    if (!Boolean(userProfile?.firstName) || userProfile?.firstName?.match(regexPatterns.alphaNumericExcludesSpace)) {
      setUserProfileErrors({
        ...userProfileErrors,
        firstName: true
      })
      return false
    }
    if (
      !isFromShopifyPublicApp &&
      (!Boolean(userProfile?.phoneNumber) ||
        userProfile?.phoneNumber?.length < 3 ||
        userProfile?.phoneNumber.length < 12)
    ) {
      setUserProfileErrors({
        ...userProfileErrors,
        phoneNumber: true
      })
      return false
    }
    if (typeof userProfile?.email === 'string' && !validateEmail(userProfile?.email)) {
      setUserProfileErrors({
        ...userProfileErrors,
        email: true
      })
      return false
    }
    return true
  }

  const handleSave = async () => {
    if (!isValid()) return
    try {
      setLoaderProperties({ isLoaderVisible: true, message: t('loading') })
      const payload = {
        first_name: userProfile?.firstName,
        last_name: userProfile?.lastName?.toString()?.length ? userProfile?.lastName : '',
        phone_number: '+'.concat(userProfile?.phoneNumber.toString()),
        role: userProfile?.role
      }
      const profileResponse: any = await putRequest('/merchant-user/profile', apiURI.AUTH_ENGINE, payload)
      setInitialUserProfile(userProfile)
      setUserCtx(profileResponse?.data?.data)
      successToast(t('changes_saved'))
    } catch (e) {
      console.error(e)
      errorToast(t('something_went_wrong'))
    } finally {
      setLoaderProperties({ isLoaderVisible: false })
    }
  }
  const onlyCountries = isFromShopifyPublicApp ? undefined : ['in']

  return (
    <>
      <div className='flex flex-col space-y-4'>
        {!isUserConfigured && (
          <div className='flex gap-4 text-[#407BA5] items-start bg-[#91C1E31A] border border-[#91C1E3] rounded p-2'>
            <div>
              <Info className='w-4 h-4' />
            </div>
            <span className={`text-xs`}>{t('user_details_mandatory')}</span>
          </div>
        )}
        <LayoutContainer>
          <div className='flex flex-row gap-2.5 pb-0.5'>
            <InputTextField
              handleFocus={() => setUserProfileErrors({ ...userProfileErrors, firstName: false })}
              label={t('first_name')}
              textFieldId='first-name'
              inputValue={userProfile?.firstName}
              handleChange={e => {
                if (e.target.value?.length > 20) return
                setUserProfile({
                  ...userProfile,
                  firstName: e.target.value
                })
              }}
              isRequired={true}
              errorStatus={Boolean(userProfileErrors?.firstName)}
              errorMessage={t('valid_first_name')}
            />

            <InputTextField
              label={t('last_name')}
              textFieldId='last-name'
              inputValue={userProfile?.lastName === 'N/A' ? '' : userProfile?.lastName}
              handleChange={e => {
                if (e.target.value?.length > 20) return
                setUserProfile({
                  ...userProfile,
                  lastName: e.target.value
                })
              }}
            />
          </div>
          <PhoneNumberInputField
            handleFocus={() => setUserProfileErrors({ ...userProfileErrors, phoneNumber: false })}
            label={t('phone_number')}
            isRequired={true}
            inputValue={userProfile?.phoneNumber}
            handleChange={e =>
              setUserProfile({
                ...userProfile,
                phoneNumber: e
              })
            }
            errorStatus={Boolean(userProfileErrors?.phoneNumber)}
            errorMessage={t('valid_phone_number')}
            onlyCountries={onlyCountries}
          />
          <div className=''>
            <div style={{ display: 'flex', flexDirection: 'column', position: 'relative' }}>
              <Typography
                variant='body2'
                sx={{
                  fontWeight: 500,
                  fontSize: 12,
                  whiteSpace: 'nowrap'
                }}
              >
                {t('email')}
                <span className='text-[#CB2711]'>*</span>
              </Typography>
              <Input className={`rounded shadow-sm`} size='small' value={userProfile?.email} disabled={true} />
            </div>
          </div>
          <div className=''>
            <label className='font-medium text-[12px] tracking-[.15px] text-[#6e6e6e] leading-normal'>
              {t('role')}
              <span className='text-[#CB2711]'>*</span>
            </label>
            <Select
              labelId='role-select-label'
              id='role-select'
              value={userProfile?.role}
              displayEmpty
              onChange={(e: any) => {
                setUserProfile({
                  ...userProfile,
                  role: e.target.value
                })
              }}
              inputProps={inputStyle}
              className='w-full h-10'
              sx={{ ...dropdownStyle, padding: 0 }}
            >
              <MenuItem value='' disabled>
                <em>Select role</em>
              </MenuItem>
              {roleValues.map((data: any) => (
                <MenuItem key={data.label} value={data.value} sx={dropdownStyle}>
                  {data.label}
                </MenuItem>
              ))}
            </Select>
          </div>
        </LayoutContainer>
      </div>
      <SaveNotification
        isActive={true}
        isVisible={JSON.stringify(initialUserProfile) !== JSON.stringify(userProfile)}
        onSave={() => {
          handleSave()
        }}
        onDiscard={() => {
          setUserProfile({ ...initialUserProfile })
          setUserProfileErrors(defaultUserProfileErrorState)
        }}
      />
    </>
  )
}

export default MyProfile
